import React, { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { InvitationCode, InvitationCodeType } from '../types/invitation';
import { InvitationService } from '../services/invitationService';
import './AdminPanel.css';

interface AdminPanelProps {
  currentUser: User;
  showToast: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
  onClose: () => void;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ currentUser, showToast, onClose }) => {
  const [invitationCodes, setInvitationCodes] = useState<InvitationCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Form state
  const [newCode, setNewCode] = useState({
    type: 'single-use' as InvitationCodeType,
    expiresInDays: 30,
    maxUses: 1,
    restrictedEmail: '',
    companyId: '',
    assignedRole: 'user' as 'owner' | 'admin' | 'manager' | 'user',
    notes: '',
  });

  useEffect(() => {
    loadInvitationCodes();
  }, [currentUser]);

  const loadInvitationCodes = async () => {
    try {
      setLoading(true);
      const codes = await InvitationService.getAllInvitationCodes();
      // Sort by creation date, newest first
      codes.sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis());
      setInvitationCodes(codes);
    } catch (error) {
      console.error('Error loading invitation codes:', error);
      showToast('Error loading invitation codes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const options: any = {
        expiresInDays: newCode.expiresInDays > 0 ? newCode.expiresInDays : undefined,
        notes: newCode.notes || undefined,
      };

      if (newCode.type === 'multi-use' && newCode.maxUses > 1) {
        options.maxUses = newCode.maxUses;
      }

      if (newCode.restrictedEmail) {
        options.restrictedEmail = newCode.restrictedEmail;
      }

      if (newCode.companyId) {
        options.companyId = newCode.companyId;
        options.assignedRole = newCode.assignedRole;
      }

      const code = await InvitationService.createInvitationCode(
        currentUser.uid,
        newCode.type,
        options
      );

      showToast(`Invitation code created: ${code.code}`, 'success');
      navigator.clipboard.writeText(code.code);
      showToast('Code copied to clipboard!', 'info');
      
      setShowCreateModal(false);
      setNewCode({
        type: 'single-use',
        expiresInDays: 30,
        maxUses: 1,
        restrictedEmail: '',
        companyId: '',
        assignedRole: 'user',
        notes: '',
      });
      loadInvitationCodes();
    } catch (error: any) {
      console.error('Error creating invitation code:', error);
      showToast('Error creating invitation code: ' + error.message, 'error');
    }
  };

  const handleRevokeCode = async (codeId: string, code: string) => {
    if (!window.confirm(`Are you sure you want to revoke code "${code}"?`)) {
      return;
    }

    try {
      await InvitationService.revokeInvitationCode(codeId);
      showToast('Invitation code revoked', 'success');
      loadInvitationCodes();
    } catch (error) {
      console.error('Error revoking code:', error);
      showToast('Error revoking code', 'error');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showToast('Code copied to clipboard!', 'info');
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    return timestamp.toDate().toLocaleDateString() + ' ' + timestamp.toDate().toLocaleTimeString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#28a745';
      case 'used': return '#6c757d';
      case 'expired': return '#ffc107';
      case 'revoked': return '#dc3545';
      default: return '#6c757d';
    }
  };

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>🔑 Invitation Code Management</h2>
        <div className="admin-header-actions">
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            + Generate New Code
          </button>
          <button onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner">Loading codes...</div>
      ) : (
        <div className="codes-list">
          <div className="codes-stats">
            <div className="stat-card">
              <span className="stat-label">Total Codes</span>
              <span className="stat-value">{invitationCodes.length}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Active</span>
              <span className="stat-value" style={{color: '#28a745'}}>
                {invitationCodes.filter(c => c.status === 'active').length}
              </span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Used</span>
              <span className="stat-value" style={{color: '#6c757d'}}>
                {invitationCodes.filter(c => c.status === 'used').length}
              </span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Revoked</span>
              <span className="stat-value" style={{color: '#dc3545'}}>
                {invitationCodes.filter(c => c.status === 'revoked').length}
              </span>
            </div>
          </div>

          <table className="codes-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Type</th>
                <th>Status</th>
                <th>Uses</th>
                <th>Created</th>
                <th>Expires</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invitationCodes.map((code) => (
                <tr key={code.id}>
                  <td>
                    <code className="code-display">{code.code}</code>
                    <button 
                      onClick={() => copyToClipboard(code.code)}
                      className="copy-btn"
                      title="Copy code"
                    >
                      📋
                    </button>
                  </td>
                  <td>
                    <span className={`badge badge-${code.type}`}>
                      {code.type}
                    </span>
                  </td>
                  <td>
                    <span 
                      className="status-badge"
                      style={{backgroundColor: getStatusColor(code.status)}}
                    >
                      {code.status}
                    </span>
                  </td>
                  <td>
                    {code.usedCount}
                    {code.maxUses && ` / ${code.maxUses}`}
                    {code.type === 'master' && ' (∞)'}
                  </td>
                  <td className="date-cell">{formatDate(code.createdAt)}</td>
                  <td className="date-cell">
                    {code.expiresAt ? formatDate(code.expiresAt) : 'Never'}
                  </td>
                  <td className="notes-cell">{code.notes || '-'}</td>
                  <td>
                    {code.status === 'active' && (
                      <button
                        onClick={() => handleRevokeCode(code.id, code.code)}
                        className="btn-danger-small"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {invitationCodes.length === 0 && (
            <div className="no-codes">
              <p>No invitation codes yet. Generate your first code!</p>
            </div>
          )}
        </div>
      )}

      {/* Create Code Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Generate Invitation Code</h3>
              <button 
                className="close-btn"
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateCode} className="modal-form">
              <div className="form-group">
                <label>Code Type</label>
                <select
                  value={newCode.type}
                  onChange={(e) => setNewCode({ ...newCode, type: e.target.value as InvitationCodeType })}
                >
                  <option value="single-use">Single Use</option>
                  <option value="multi-use">Multi Use</option>
                  <option value="master">Master (Unlimited)</option>
                </select>
                <small className="help-text">
                  Single-use: One person only | Multi-use: Limited uses | Master: Unlimited uses
                </small>
              </div>

              {newCode.type === 'multi-use' && (
                <div className="form-group">
                  <label>Maximum Uses</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={newCode.maxUses}
                    onChange={(e) => setNewCode({ ...newCode, maxUses: parseInt(e.target.value) })}
                  />
                </div>
              )}

              <div className="form-group">
                <label>Expires In (Days)</label>
                <input
                  type="number"
                  min="0"
                  max="365"
                  value={newCode.expiresInDays}
                  onChange={(e) => setNewCode({ ...newCode, expiresInDays: parseInt(e.target.value) })}
                />
                <small className="help-text">0 = Never expires</small>
              </div>

              <div className="form-group">
                <label>Link to Company (Optional)</label>
                <input
                  type="text"
                  value={newCode.companyId}
                  onChange={(e) => setNewCode({ ...newCode, companyId: e.target.value })}
                  placeholder="Company ID (e.g., comp_abc_123)"
                />
                <small className="help-text">
                  💡 If set, user automatically joins this company. Get ID from Master Control Panel.
                </small>
              </div>

              {newCode.companyId && (
                <div className="form-group">
                  <label>Assigned Role in Company</label>
                  <select
                    value={newCode.assignedRole}
                    onChange={(e) => setNewCode({ ...newCode, assignedRole: e.target.value as any })}
                  >
                    <option value="admin">Admin (Full access)</option>
                    <option value="manager">Manager (Manage contracts)</option>
                    <option value="user">User (Basic access)</option>
                  </select>
                  <small className="help-text">
                    Role user will have in the company
                  </small>
                </div>
              )}

              <div className="form-group">
                <label>Restricted Email (Optional)</label>
                <input
                  type="email"
                  value={newCode.restrictedEmail}
                  onChange={(e) => setNewCode({ ...newCode, restrictedEmail: e.target.value })}
                  placeholder="user@example.com"
                />
                <small className="help-text">Leave empty to allow any email</small>
              </div>

              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  value={newCode.notes}
                  onChange={(e) => setNewCode({ ...newCode, notes: e.target.value })}
                  placeholder="e.g., Code for John Doe, Client ABC, etc."
                  rows={3}
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Generate Code
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;


